import { useEffect, useState } from 'react'
import { NavLink, useNavigate } from 'react-router-dom'
import { useMsal } from '@azure/msal-react'
import {
  tokens,
  Text,
  Avatar,
  Button,
  Menu,
  MenuTrigger,
  MenuPopover,
  MenuList,
  MenuItem,
} from '@fluentui/react-components'
import {
  Certificate20Regular,
  Gauge20Regular,
  History20Regular,
  MoreHorizontal20Regular,
  People20Regular,
  Settings20Regular,
  SignOut20Regular,
} from '@fluentui/react-icons'
import { apiFetchJson } from '../lib/apiFetch'
import { useMe, canAccessService } from '../lib/me'
import { SERVICE_LABELS, SERVICE_INGESTION, INGESTION_BADGE } from '../lib/services'
import Lozenge from './Lozenge'
import ServiceIcon from './ServiceIcon'
import type { ServiceConfigResponse } from '../types'

const SIDEBAR_WIDTH = 232

interface NavItemProps {
  to: string
  end?: boolean
  indent?: boolean
  icon?: React.ReactNode
  badge?: React.ReactNode
  children: React.ReactNode
}

function NavItem({ to, end, indent, icon, badge, children }: NavItemProps) {
  return (
    <NavLink
      to={to}
      end={end}
      style={({ isActive }) => ({
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        padding: indent ? '6px 12px 6px 40px' : '8px 12px',
        borderRadius: tokens.borderRadiusMedium,
        textDecoration: 'none',
        fontSize: indent ? 13 : 14,
        fontWeight: isActive ? 600 : 400,
        color: isActive ? tokens.colorBrandForeground1 : tokens.colorNeutralForeground2,
        background: isActive ? tokens.colorNeutralBackground1Selected : 'transparent',
      })}
    >
      {icon}
      <span style={{ flex: 1 }}>{children}</span>
      {badge}
    </NavLink>
  )
}

export default function Sidebar() {
  const navigate = useNavigate()
  const { instance, accounts } = useMsal()
  const account = accounts[0]
  const me = useMe()
  const [serviceConfig, setServiceConfig] = useState<ServiceConfigResponse['config']>({})

  useEffect(() => {
    apiFetchJson<ServiceConfigResponse>('/api/service-config')
      .then(res => setServiceConfig(res.config))
      .catch(() => {})
  }, [])

  return (
    <nav
      style={{
        width: SIDEBAR_WIDTH,
        flexShrink: 0,
        borderRight: `1px solid ${tokens.colorNeutralStroke2}`,
        padding: '20px 12px',
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
        height: '100%',
        overflowY: 'auto',
      }}
    >
      <div style={{ padding: '0 12px', marginBottom: 20 }}>
        <Text weight="bold" size={500} block>
          RG StackRadar
        </Text>
        <Text size={200} style={{ color: tokens.colorNeutralForeground3 }}>
          v{__APP_VERSION__}
        </Text>
      </div>

      <NavItem to="/dashboard" icon={<Gauge20Regular />}>
        Dashboard
      </NavItem>
      <NavItem to="/" end icon={<Certificate20Regular />}>
        Licenses
      </NavItem>
      {Object.entries(SERVICE_LABELS)
        .filter(([serviceId]) => canAccessService(me, serviceId))
        .map(([serviceId, label]) => {
          const ingestion = INGESTION_BADGE[SERVICE_INGESTION[serviceId]]
          return (
            <NavItem
              key={serviceId}
              to={`/services/${serviceId}`}
              indent
              icon={<ServiceIcon config={serviceConfig[serviceId]} size={16} />}
              badge={
                ingestion && (
                  <Lozenge color={ingestion.color} size="small">
                    {ingestion.label}
                  </Lozenge>
                )
              }
            >
              {label}
            </NavItem>
          )
        })}

      {me.isAdmin && (
        <NavItem to="/users" end icon={<People20Regular />}>
          Users
        </NavItem>
      )}
      {canAccessService(me, 'peopleforce') && (
        <NavItem
          to="/users/people-force"
          indent
          icon={<ServiceIcon config={serviceConfig.peopleforce} size={16} />}
          badge={
            <Lozenge color={INGESTION_BADGE.api.color} size="small">
              {INGESTION_BADGE.api.label}
            </Lozenge>
          }
        >
          PeopleForce
        </NavItem>
      )}
      {canAccessService(me, 'github') && (
        <NavItem
          to="/users/github"
          indent
          icon={<ServiceIcon config={serviceConfig.github} size={16} />}
          badge={
            <Lozenge color={INGESTION_BADGE.api.color} size="small">
              {INGESTION_BADGE.api.label}
            </Lozenge>
          }
        >
          GitHub
        </NavItem>
      )}

      <div
        style={{
          marginTop: 'auto',
          paddingTop: 12,
          borderTop: `1px solid ${tokens.colorNeutralStroke2}`,
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          padding: '12px 8px 0',
        }}
      >
        <Avatar name={account?.name ?? account?.username} size={28} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <Text block wrap={false} truncate style={{ fontSize: 13, fontWeight: 600 }}>
            {account?.name ?? account?.username ?? 'Signed in'}
          </Text>
          {account?.username && (
            <Text block wrap={false} truncate size={200} style={{ color: tokens.colorNeutralForeground3 }}>
              {account.username}
            </Text>
          )}
        </div>
        <Menu>
          <MenuTrigger disableButtonEnhancement>
            <Button appearance="subtle" icon={<MoreHorizontal20Regular />} aria-label="More options" />
          </MenuTrigger>
          <MenuPopover>
            <MenuList>
              {me.isAdmin && (
                <MenuItem icon={<Settings20Regular />} onClick={() => navigate('/settings')}>
                  Settings
                </MenuItem>
              )}
              {me.isAdmin && (
                <MenuItem icon={<History20Regular />} onClick={() => navigate('/logs')}>
                  Logs
                </MenuItem>
              )}
              <MenuItem icon={<SignOut20Regular />} onClick={() => instance.logoutRedirect()}>
                Sign out
              </MenuItem>
            </MenuList>
          </MenuPopover>
        </Menu>
      </div>
    </nav>
  )
}
